import { fabric } from 'fabric';
import { iconDefaultOffset, iconDefaultSize } from '../../../constants';
import { CanvasDataTypes } from '../../../utils';
import { handleImageWithIconMoves } from './handle-image-with-icon-moves';
import { handleImageWithIconResizes } from './handle-image-with-icon-resizes';

export enum IconsTypes {
  LowRes = 'lowRes',
}

const ICON_LOAD_TIMEOUT_MS = 30_000;

type AddIconOptions = {
  iconOffset?: number;
  isStaticIcon?: boolean;
  isLowResIcon?: boolean;
  onIconAdded?: (icon: fabric.Image) => void;
  photoZone?: fabric.Object;
};

/**
 * Add an icon to the corner of a fabric object, such as a photo.
 * @param canvas - fabric canvas
 * @param parentObject - fabric object
 * @param iconPath - path to the icon
 * @param iconType - type of icon
 * @param options - additional options for the icon
 */
export const addIconToObject = (
  canvas: fabric.Canvas,
  object: fabric.Object,
  iconPath: string,
  iconType: string,
  options: AddIconOptions = {},
): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const { iconOffset = iconDefaultOffset, isStaticIcon, isLowResIcon, onIconAdded, photoZone } = options;
    const loadState = { settled: false };
    const iconListenersCleanup: { current: () => void } = { current: () => undefined };
    const handleParentRemoved = () => {
      if (loadState.settled) return;
      loadState.settled = true;
      clearTimeout(loadTimeout);
      object.off('removed', handleParentRemoved);
      resolve();
    };
    const loadTimeout = setTimeout(() => {
      if (loadState.settled) return;
      loadState.settled = true;
      object.off('removed', handleParentRemoved);
      reject(new Error('Timed out loading editor icon'));
    }, ICON_LOAD_TIMEOUT_MS);
    object.on('removed', handleParentRemoved);

    try {
      fabric.Image.fromURL(iconPath, (icon: fabric.Image, isError?: boolean) => {
        if (loadState.settled) return;
        loadState.settled = true;
        clearTimeout(loadTimeout);
        object.off('removed', handleParentRemoved);

        try {
          if (isError || !icon) {
            throw new Error('Unable to load editor icon');
          }

          const objectListeners: Array<{ event: string; handler: () => void }> = [];
          const handleMouseUp = () => {
            if (icon.opacity !== 1) {
              icon.set({ opacity: 1 });
              canvas.renderAll();
            }
          };
          const removeIconListeners = () => {
            objectListeners.forEach(({ event, handler }) => object.off(event, handler));
            object.off('removed', removeIconListeners);
            canvas.off('mouse:up', handleMouseUp);
          };
          iconListenersCleanup.current = removeIconListeners;
          const addObjectListener = (event: string, handler: () => void) => {
            object.on(event, handler);
            objectListeners.push({ event, handler });
          };

          const iconSize = iconDefaultSize;
          icon.set({
            selectable: false,
            originX: 'left',
            originY: 'top',
            data: {
              type: CanvasDataTypes.PhotoIcon,
              removableOnSave: true,
              parentName: object.name,
            },
          });

          if (photoZone) {
            icon.set({
              left: (photoZone.left ?? 0) + iconOffset,
              top: (photoZone.top ?? 0) + iconOffset,
            });
          } else if (object.left != null && object.top != null) {
            icon.set({
              left: object.left + iconOffset,
              top: object.top + iconOffset,
            });
          }

          icon.scaleToWidth(iconSize);
          icon.scaleToHeight(iconSize);

          handleImageWithIconResizes(icon, object, iconOffset, isStaticIcon, isLowResIcon);
          if (!isStaticIcon) {
            addObjectListener('moving', () => handleImageWithIconMoves(icon, object, iconOffset));
            addObjectListener('rotating', () => handleImageWithIconMoves(icon, object, iconOffset));
          }
          addObjectListener('moving', () => {
            icon.set({ opacity: 0.6 });
          });
          addObjectListener('scaling', () =>
            handleImageWithIconResizes(icon, object, iconOffset, isStaticIcon, isLowResIcon),
          );
          object.on('removed', removeIconListeners);
          canvas.on('mouse:up', handleMouseUp);
          object.set({ data: { ...object.data, iconType: iconType } });
          canvas.add(icon.bringToFront());
          onIconAdded?.(icon);
          resolve();
        } catch (error) {
          iconListenersCleanup.current();
          reject(error);
        }
      });
    } catch (error) {
      if (loadState.settled) return;
      loadState.settled = true;
      clearTimeout(loadTimeout);
      object.off('removed', handleParentRemoved);
      reject(error);
    }
  });
